import * as React from 'react';
import { Outlet, createRootRouteWithContext, useNavigate, useRouterState, type ErrorComponentProps } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@viewstream/ui';
import { ApiError } from '@viewstream/api-client';
import { unauthorized } from '~/app/api';
import { reportError } from '~/app/telemetry';
import { isChunkError, reloadOnce } from '~/app/chunkReload';
import { recordUiError } from '~/app/supportContext';

export interface RouterCtx { app: 'studio' | 'admin' }

export const Route = createRootRouteWithContext<RouterCtx>()({
  component: RootLayout,
  errorComponent: RootError,
});

function RootLayout() {
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.href });
  // any 401 → login with the return URL (foundation spec "Session expired mid-session")
  React.useEffect(() => {
    const h = () => {
      if (!path.startsWith('/login')) void navigate({ to: '/login', search: { next: path } });
    };
    unauthorized.addEventListener('unauthorized', h);
    return () => unauthorized.removeEventListener('unauthorized', h);
  }, [navigate, path]);
  return <Outlet />;
}

function RootError({ error, reset }: ErrorComponentProps) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const chunk = isChunkError(error);
  const message = error instanceof Error ? error.message : String(error);
  const requestId = error instanceof ApiError ? error.requestId : null;
  // change support-requests: "Contact support" from the error page, inside a tenant (/t/<slug>/…): category bug, the
  // error text and its request id pre-filled, the page attached
  const slug = /^\/t\/([^/]+)/.exec(window.location.pathname)?.[1];
  const contactSupport = () => {
    if (!slug) return;
    void navigate({ to: '/t/$slug/support', params: { slug: decodeURIComponent(slug) },
      search: { category: 'bug', from: window.location.pathname, error: message.slice(0, 300), ...(requestId ? { rid: requestId } : {}) } } as never);
  };
  // a route chunk of the previous build (redeploy while the tab was open): reload once to get the new build
  React.useEffect(() => {
    if (chunk) reloadOnce();
    else { reportError(error); recordUiError(message, requestId); }
  }, [error, chunk, message, requestId]);
  return (
    <main className="mx-auto mt-24 max-w-lg p-6 text-center" role="alert">
      <h1 className="text-xl font-semibold">{t`Something went wrong — reload the page`}</h1>
      <p className="mt-2 text-sm text-fg-muted">{chunk ? t`Studio was updated while this page was open.` : error instanceof Error ? error.message : String(error)}</p>
      <div className="mt-4 flex justify-center gap-2">
        <Button onClick={() => window.location.reload()}>{t`Reload`}</Button>
        {!chunk ? <Button variant="secondary" onClick={reset}>{t`Try again`}</Button> : null}
        {!chunk && slug ? <Button variant="ghost" onClick={contactSupport} data-testid="error-contact-support">{t`Contact support`}</Button> : null}
      </div>
    </main>
  );
}
